<template>
  <div>
    <SignUpHeader />

    <div class="tw-flex tw-flex-wrap tw-min-h-screen tw-items-stretch tw-w-full tw-py-10">
      <div class="tw-w-full md:tw-w-5/6 lg:tw-w-2/3 px-body md:tw-px-0 tw-h-full tw-flex tw-flex- tw-flex-col tw-justify-center tw-items-center">
        <form
          id="signup-form"
          ref="signup-form"
          class="tw-max-w-full md:tw-w-2/3 tw-flex-shrink-0 tw-mb-20 auth-card tw-flex tw-flex-wrap"
          method="post"
          action="/signup"
          @submit.prevent="onSubmit"
        >
          <UserSignUp @input="updateForm" title="Mentee Sign Up" />

          <div class="tw-w-full tw-pt-6 tw-mb-4 tw-flex-shrink-0 tw-px-1">
            <h2 class="font-title tw-text-xl tw-mb-1">2. Mentee details</h2>
          </div>
          <template v-if="isScopeApp">
            <div class="tw-w-full tw-mb-4 tw-flex-shrink-0 tw-px-1">
              <SelectGroup
                :class="{ '--loading-data': pageIsLoading }"
                label="Mentee category"
                name="goal"
                nameKey="goal_group"
                placeholder="Goal"
                input-classes="my-select--huge tw-bg-gray-200"
                v-model="profession"
                :options="professionList"
                :reduce="profession => profession.id"
                :selected="profession"
                :clearable="false"
                :error="errors.profession"
              />
            </div>
          </template>
          <div class="tw-w-full tw-mb-4 tw-flex-shrink-0 tw-px-1">
            <SelectGroup
              :class="{ '--loading-data': pageIsLoading }"
              label="Availability"
              name="availability"
              instruct="You can add multiple values"
              nameKey="availability"
              placeholder="Availability"
              input-classes="my-select--huge tw-font-sans tw-text-base tw-bg-gray-200 tw-pt-1"
              v-model="availability"
              :options="availabilityList"
              :reduce="availability => availability.id"
              :multiple="true"
              :clearable="true"
              :error="errors.availability"
            />
          </div>
          <template v-if="isScopeApp">
            <div class="tw-w-full tw-pt-12 tw-mb-4 tw-flex-shrink-0 tw-px-1">
              <h2 class="font-title tw-text-xl tw-mb-1">3. Secret key</h2>
            </div>
            <div class="tw-w-full tw-mb-2 tw-flex-shrink-0 tw-px-1">
              <InputGroup
                label="Company secret key given by admin"
                name="secret"
                type="text"
                placeholder="* * * *"
                input-classes="tw-p-3"
                v-model="secret"
                :error="errors.secret"
              >
                <template slot="message">
                  <p class="tw-text-sm tw-mt-2">
                    You are registering under a company's mentorship program. If this is a mistake,
                    <a class="tw-text-blue-500 tw-underline" :href="`https://ementored.com/${$route.path}`">try the main app.</a>
                  </p>
                </template>
              </InputGroup>
            </div>
          </template>
          <div class="tw-w-full tw-flex-shrink-0 tw-px-1 tw-mb-4 tw-pt-20">
            <BaseButton
              class="tw-block tw-w-full md:tw-inline-block md:tw-w-auto tw-mb-4 tw-py-4 tw-px-8"
              type="submit"
              :text="btnText"
              :disabled="!canSubmit || btnDisabled"
            />
            <router-link class="
              tw-inline-block sm:tw-ml-6 tw-py-3 tw-align-baseline tw-font-bold tw-text-sm tw-text-blue-500
              my-btn tw-border-0
              hover:tw-text-blue-800"
              to="login"
            >I have an account</router-link>
          </div>
        </form>
      </div>
      <div class="tw-hidden md:tw-block tw-max-w-xs tw-ml-auto md:tw-w-1/6 lg:tw-w-1/3 tw-bg-gray-100 my-bg-pattern tw-rounded-l-55"></div>
    </div>
  </div>
</template>

<script>
import UserSignUp from '@/views/_partials/UserSignUp.vue';
import SignUpHeader from '@/views/_partials/SignUpHeader.vue';
import InputGroup from '@/components/InputGroup.vue';
import SelectGroup from '@/components/SelectGroup.vue';
import form from '@/modules/formHelper';
import { apiGet, apiPost, generateFormData } from '@/modules/apiHelper';
import signupMixin from '@/mixins/signup';
import visitorsMixin from '@/mixins/visitors';

export default {
  name: 'Signup',
  components: {
    UserSignUp,
    InputGroup,
    SelectGroup,
    SignUpHeader,
  },
  mixins: [signupMixin, visitorsMixin],
  data() {
    return {
      pageIsLoading: true,
    };
  },
  watch: {
    profession(val) {
      if (!form.isValidSelectInput(val)) {
        this.errors.profession = 'Mentee category is required';
      } else {
        this.errors.profession = undefined;
      }
    },
  },
  computed: {
    canSubmit() {
      return form.isValidTextInput(this.userForm.fname)
        && form.isValidTextInput(this.userForm.sname)
        && form.isValidEmail(this.userForm.email)
        && form.isValidTextInput(this.userForm.password, true, form.passwordRegex)
        && form.passwordsMatch(this.userForm.password, this.userForm.confirmPassword)
        && form.isValidNumber(this.userForm.phone, false)
        && form.isValidSelectInput([
          this.userForm.gender,
          this.isScopeApp ? this.profession : '1',
        ])
        && this.availability.length > 0
        && (this.isScopeApp ? !!this.secret : true);
    },
    formData() {
      const formData = new FormData();

      formData.set('fname', this.userForm.fname);
      formData.set('sname', this.userForm.sname);
      formData.set('working_num', `${this.userForm.phone}`[0] === '+' ? `${this.userForm.phone}`.substr(1) : this.userForm.phone);
      formData.set('email', this.userForm.email);
      formData.set('password', this.userForm.password);
      formData.set('gender', this.userForm.gender);
      if (this.profession) {
        formData.set('profession', this.profession);
      }
      formData.set('availability', JSON.stringify(this.availability));
      formData.set('type', 4);
      formData.set('level', 4);
      formData.set('companyid', this.scopeId);
      if (this.secret) {
        formData.set('secret', this.secret);
      }

      return formData;
    },
  },
  async mounted() {
    // get scope id
    await this.$store.dispatch('setScope');
    // eslint-disable-next-line prefer-destructuring
    this.isMainApp = (await this.$store.dispatch('evaluateScope'))[1];
    this.scopeId = this.$store.getters.scopeId;

    const requests = [
      ['company-pillars', 'professionList'],
      ['availability', 'availabilityList'],
    ];
    let requestsErrorCount = 0;

    Promise.all([
      apiPost('company-pillars', generateFormData({ companyid: this.scopeId }), 5),
      apiGet('availability'),
    ])
      .catch((err) => {
        console.warn(err);
        this.$toasted.global.appError({ duration: 10000 });
        this.pageIsLoading = true;
      })
      .then((res) => {
        if (!res) return;
        res.forEach(({ data, error }, i) => {
          if (error) {
            console.error('error in fetching... ', res[i]);
            return;
          }
          if (!data[requests[i][0]]) {
            console.error(`data not found for :: ${requests[i]}`);
            requestsErrorCount += 1;
            return;
          }

          this[requests[i][1]] = data[requests[i][0]];
        });

        if (requestsErrorCount === 0) {
          this.pageIsLoading = false;
        }
      });
  },
};
</script>

<style>

</style>
